// Single source of truth for all user-facing copy and CTAs.
// NEVER use AI/IA/inteligência artificial in any visible copy.

export const COPY = {
  brand: {
    name: 'Professya',
    tagline: 'Astrologia Personalizada',
    description: 'O céu não fala com o seu signo. Fala com você.',
  },

  hero: {
    eyebrow: 'Astrologia personalizada · desde 2024',
    headline: 'O céu não fala\ncom o seu signo.',
    headlineEmphasis: 'Fala com você.',
    subheadline:
      'Mapa natal completo, doze sistemas de leitura e uma interpretação feita para o seu céu, não o do seu signo.',
    ctaPrimary: 'Ver meu mapa natal grátis',
    ctaSecondary: 'Como funciona',
    proofLabel: 'pessoas já mapearam seu céu',
    proofFallback: 12_000,
  },

  pricing: {
    eyebrow: 'Assinatura',
    heading: 'Escolha a sua leitura.',
    guarantee: 'Cancele quando quiser · Sem fidelidade',
    guaranteeLong: '',
    plans: {
      inicial: {
        name: 'Inicial',
        price: 'Grátis',
        billing: 'para sempre',
        cta: 'Começar grátis',
        features: [
          'Mapa natal completo',
          'Previsão diária',
          '3 perguntas por mês',
          'Tarô básico',
        ],
        muted: ['Sinastria', 'I Ching', 'Numerologia avançada', 'Exportação PDF'],
      },
      premium: {
        name: 'Premium',
        badge: 'Mais Popular',
        price: 'R$ 24,90',
        billing: '/mês',
        cta: 'Começar minha leitura',
        features: [
          'Tudo do Inicial',
          'Sinastria ilimitada',
          'I Ching + Numerologia',
          'Exportação PDF',
          'Previsão semanal e mensal',
          'Histórico completo',
        ],
        muted: [],
      },
      anual: {
        name: 'Anual',
        price: 'R$ 199,90',
        billing: '/ano',
        billingNote: 'equivale a R$ 16,66/mês',
        savings: 'Economize R$ 99/ano vs mensal',
        roiHint: 'Menos que uma consulta com astrólogo',
        cta: 'Começar com desconto',
        features: [
          'Tudo do Premium',
          'Astrocartografia',
          'Quiromancia',
          'Acesso antecipado a novidades',
        ],
        muted: [],
      },
    },
  },

  faq: {
    eyebrow: 'Perguntas',
    heading: 'Dúvidas frequentes.',
    searchPlaceholder: 'Buscar pergunta…',
    items: [
      {
        starred: true,
        q: 'A leitura é personalizada ou genérica por signo?',
        a: 'Cada leitura é gerada especificamente para você com base no seu mapa natal. Não existe texto pronto. Usamos as posições planetárias reais do seu nascimento (Sol, Lua, Ascendente, 12 casas, planetas e aspectos) para gerar uma interpretação única para você.',
      },
      {
        starred: true,
        q: 'Funciona se eu não souber meu horário de nascimento?',
        a: 'Sim. A hora é opcional. Sem ela, calculamos o mapa com base na data e cidade. Você terá Sol, Lua, planetas e a maioria das casas. O Ascendente precisa da hora para máxima precisão.',
      },
      {
        starred: true,
        q: 'Posso cancelar quando quiser?',
        a: 'Sim, sem burocracia. Você solicita cancelamento pelo app ou pelo site a qualquer momento. Verifique a política de cancelamento.',
      },
      {
        starred: true,
        q: 'Aceita Pix e outros pagamentos?',
        a: 'Sim. Aceitamos Pix, cartão de crédito (Visa, Mastercard, Elo, Amex) e boleto bancário. Os pagamentos são processados com segurança.',
      },
      {
        starred: true,
        q: 'Meus dados estão seguros?',
        a: 'Seus dados ficam armazenados com criptografia e nunca são vendidos a terceiros. Seguimos a LGPD rigorosamente. Você pode solicitar exclusão total dos seus dados a qualquer momento.',
      },
      {
        starred: false,
        q: 'Funciona bem no celular?',
        a: 'Sim. A Professya é otimizada para celular, desenvolvida como Progressive Web App e disponível como app nativo para iOS e Android.',
      },
      {
        starred: false,
        q: 'Qual a diferença entre Professya e leitura de horóscopo?',
        a: 'Horóscopo usa apenas o seu signo solar, 1 de 12 posições possíveis. A Professya usa seu mapa natal completo: todos os planetas, casas e aspectos calculados para o momento exato do seu nascimento. São centenas de variáveis únicas, assim como a sua impressão digital.',
      },
    ],
  },

  testimonials: {
    eyebrow: 'Resultados Reais',
    heading: 'Quem já descobriu seu mapa.',
    items: [
      {
        name: 'Mariana S.',
        location: 'São Paulo, SP',
        stars: 5,
        text: 'Nunca me identifiquei com horóscopo. Aqui finalmente me sinto vista de verdade. A leitura do meu Ascendente Escorpião com Lua em Câncer foi exatamente o que eu precisava ouvir.',
        plan: 'Premium',
      },
      {
        name: 'Renata O.',
        location: 'Belo Horizonte, MG',
        stars: 5,
        text: 'Usei para entender a sinastria com meu marido. Os aspectos Venus-Marte explicaram tudo sobre nossa dinâmica de relacionamento. Valeu cada centavo.',
        plan: 'Anual',
      },
      {
        name: 'Priscila M.',
        location: 'Curitiba, PR',
        stars: 5,
        text: 'O I Ching que recebi numa terça difícil foi tão preciso que chorei. Não é algo genérico, é realmente para mim.',
        plan: 'Premium',
      },
      {
        name: 'Carolina F.',
        location: 'Rio de Janeiro, RJ',
        stars: 5,
        text: 'Comecei com o plano grátis e em 3 dias assinei o anual. A previsão semanal mudou como eu planejo minha semana de trabalho.',
        plan: 'Anual',
      },
      {
        name: 'Juliana T.',
        location: 'Florianópolis, SC',
        stars: 5,
        text: 'A leitura de Numerologia Cabalística foi incrível. Descobri padrões que se repetem na minha vida e agora entendo por quê.',
        plan: 'Premium',
      },
      {
        name: 'Amanda L.',
        location: 'Porto Alegre, RS',
        stars: 5,
        text: 'Indiquei para minha mãe e minha irmã. As três assinamos juntas. É uma ferramenta de autoconhecimento que não tem igual.',
        plan: 'Anual',
      },
    ],
  },

  exitIntent: {
    heading: 'Espera, não vá ainda.',
    subheading: 'Seu mapa natal está esperando por você.',
    offer: '20% OFF no primeiro mês',
    ctaPrimary: 'Resgatar desconto',
    ctaDismiss: 'Não, obrigada',
    coupon: 'BEMVINDA20',
  },

  whatsapp: {
    number: '5511999999999',
    message: 'Olá! Vim pelo site e tenho uma dúvida sobre a Professya.',
    label: 'Falar no WhatsApp',
  },

  footer: {
    tagline: 'Astrologia personalizada para você, não genérica para o seu signo.',
    email: 'contato@professya.com',
  },
} as const;
